import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * TotalBalanceCard Component
 * 
 * Displays the total balance across all accounts with percentage change
 * Uses a custom hook for data fetching and management
 * 
 * @returns {JSX.Element} TotalBalanceCard component
 */
const TotalBalanceCard = ({ balance = 0, percentageChange = 0, isLoading }) => {
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Mobile: StatsCard-style layout */}
      <div className="sm:hidden flex flex-col items-center mb-1 flex-shrink-0">
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/90 p-1 rounded-lg mb-1 shadow-md flex-shrink-0">
          <svg className="h-3 w-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <div className="min-w-0 w-full flex flex-col items-center gap-0.5">
          <h3 className="text-[10px] font-medium text-gray-600 text-center leading-tight px-0.5">Total Balance</h3>
          <div className="relative group flex-shrink-0">
            <InformationCircleIcon className="h-2.5 w-2.5 text-gray-400 hover:text-gray-600 cursor-help" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg invisible group-hover:visible transition-all pointer-events-none z-10">
              <p className="font-normal leading-relaxed">
                🎯 Your money snapshot! This is how much cash you've got across all your accounts right now.
              </p>
              <p className="mt-2 text-gray-300 text-xs">
                Example: Checking ($1,000) + Savings ($5,000) - Credit Card (-$500) = $5,500 Total Balance
              </p>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile: Centered value */}
      <div className="sm:hidden flex-1 flex flex-col justify-center items-center">
        <div className="text-[10px] sm:text-xs font-bold text-emerald-600 drop-shadow-sm break-words leading-tight text-center px-0.5">
          ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-[9px] text-gray-500 mt-0.5 break-words leading-tight text-center px-0.5">
          {percentageChange >= 0 ? '+' : ''}{percentageChange.toFixed(2)}%
        </div>
      </div>

      {/* Desktop: Original layout */}
      <div className="hidden sm:block">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-1">
            <h3 className="text-gray-500 text-xs sm:text-sm">Total Balance</h3>
            <div className="relative group">
              <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg invisible group-hover:visible transition-all pointer-events-none z-10">
                <p className="font-normal">
                  🎯 Your money snapshot! This is how much cash you've got across all your accounts right now.
                </p>
                <p className="mt-2 text-gray-300 text-xs">
                  Example: Checking ($1,000) + Savings ($5,000) - Credit Card (-$500) = $5,500 Total Balance
                </p>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>
          <div className={`text-xs sm:text-sm ${percentageChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {percentageChange >= 0 ? '+' : ''}{percentageChange.toFixed(2)}%
          </div>
        </div>
        <div className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800 text-center">
          ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
    </div>
  );
};

export default TotalBalanceCard; 